// @flow

import React, { Fragment } from 'react';

import { Link } from '@zapier/design-system';

import help from 'app/get-help/components/blocks/Help';
import radio from 'app/get-help/components/blocks/Radio';
import zap from 'app/get-help/components/blocks/Zap';
import {
  getCriticalIssueFields,
  getDefaultFields,
  getUserFields,
  messageField,
  taskField,
} from 'app/get-help/content/nodes/common';
import { ZENDESK_CUSTOM_FIELD_IDS } from 'app/get-help/constants';

export const createPlan = (isLoggedIn: boolean) => ({
  value: 'plan',
  label: 'Choosing the right pricing plan',
  zendeskCustomFieldValue: 'cf-plan-selection',
  child: help({
    docs: {
      title: 'Consider how you’ll use Zapier',
      body: () => (
        <Fragment>
          <p>Pricing at Zapier is based off several features, mainly:</p>
          <ol>
            <li>
              How many{' '}
              <Link href="https://help.zapier.com/hc/en-us/articles/8496309697421">
                Zaps
              </Link>{' '}
              you need
            </li>
            <li>
              How many{' '}
              <Link href="https://help.zapier.com/hc/en-us/articles/8496196837261">
                Tasks
              </Link>{' '}
              you use per month
            </li>
            <li>
              Access to <Link href="/apps?filter=premium">Premium apps</Link>
            </li>
          </ol>
          <p>
            For more detailed information about Zapier plans and other useful
            features available, check out our{' '}
            <Link href="/app/planbuilder/plans">pricing page</Link>.
          </p>
        </Fragment>
      ),
      links: [
        {
          href: '/app/planbuilder/plans',
          title: 'How do I view or change my plan?',
        },
        {
          href: 'https://help.zapier.com/hc/en-us/articles/8496277302157',
          title: 'What happens when I change my plan?',
        },
      ],
    },
    contact: [
      ...getUserFields(isLoggedIn),
      {
        ...messageField,
        details: `Please let us know the apps you're interested in, the kinds of workflows you'd like to automate, as well as how much volume you'd expect to see from your apps each month.`,
      },
      ...getCriticalIssueFields(isLoggedIn),
    ],
  }),
});

export const createDiscounts = (isLoggedIn: boolean) => ({
  value: 'discounts',
  label: 'Discounts',
  zendeskCustomFieldValue: 'cf-discounts',
  child: help({
    docs: {
      title: "Learn about Zapier's nonprofit and annual plan discounts",
      body: () => (
        <p>
          To see discounts you might qualify for,{' '}
          <Link href="https://help.zapier.com/hc/en-us/articles/8496197165581">
            check out our discounts page
          </Link>
          . We offer discounts for being a non-profit as well as if you purchase
          an annual plan with us.
        </p>
      ),
    },
    contact: getDefaultFields(isLoggedIn),
  }),
});

export const createTeams = (isLoggedIn: boolean) => ({
  value: 'teams',
  label: 'Team accounts',
  zendeskCustomFieldValue: 'cf-team-accounts',
  child: help({
    docs: {
      title: 'Learn about Zapier for Teams',
      body: () => (
        <p>
          To learn more about Zapier for Teams,{' '}
          <Link href="https://help.zapier.com/hc/en-us/articles/8496258566285-Create-a-Zapier-for-Teams-account">
            check out our Zapier for Teams page
          </Link>
          . Here you can learn what benefits the Team account has (such as
          sharing Zaps and usage-based billing), sign up for a Team trial, and
          get answers to frequently asked questions.
        </p>
      ),
    },
    contact: getDefaultFields(isLoggedIn),
  }),
});

export const createEnterprise = (isLoggedIn: boolean) => ({
  value: 'enterprise',
  label: 'Enterprise accounts',
  zendeskCustomFieldValue: 'cf-enterprise-accounts',
  child: help({
    docs: {
      title: 'Learn about Zapier for Enterprise',
      body: () => (
        <p>
          To learn more about Zapier for Enterprise,{' '}
          <Link href="https://help.zapier.com/hc/en-us/articles/8496213575053">
            check out our Zapier for Enterprise page
          </Link>
          . You'll learn about the features that are only available for
          Enterprise accounts, see answers to frequently asked questions, and be
          able to sign up for an Enterprise plan.
        </p>
      ),
    },
    contact: getDefaultFields(isLoggedIn),
  }),
});

export const createBilling = (isLoggedIn: boolean) => ({
  value: 'billing',
  label: 'Managing your billing information',
  zendeskCustomFieldValue: 'cf-manage-billing',
  child: radio({
    id: 'billing',
    label: "What's the issue?",
    zendeskCustomFieldId:
      ZENDESK_CUSTOM_FIELD_IDS.BILLING_WHAT_WOULD_YOU_LIKE_TO_DO,
    items: [
      {
        value: 'change',
        label: 'Change plans or cancel',
        zendeskCustomFieldValue: 'cf-change-or-cancel',
        child: help({
          docs: {
            title: 'Change your plan in your billing settings',
            body: () => (
              <p>
                You can always change your plan{' '}
                <Link href="/app/planbuilder/plans">here</Link>. There, you can
                choose to be billed annually or monthly, as well as upgrade or
                downgrade. If you want to cancel, you can do that by downgrading
                to the free plan, so you won't be charged.
              </p>
            ),
          },
          contact: getDefaultFields(isLoggedIn),
        }),
      },
      {
        value: 'invoices',
        label: 'View invoices',
        zendeskCustomFieldValue: 'cf-view-invoices',
        child: help({
          docs: {
            title: 'View your invoices in your billing settings',
            body: () => (
              <p>
                You can view your past and upcoming invoices in your{' '}
                <Link href="/app/settings/billing">billing settings here</Link>.
                You can also print invoices out and change your invoice
                information.
              </p>
            ),
          },
          contact: getDefaultFields(isLoggedIn),
        }),
      },
      {
        value: 'date',
        label: 'Change billing date',
        zendeskCustomFieldValue: 'cf-change-billing-date',
        child: help({
          docs: {
            title: 'Change your plan on your preferred billing date',
            body: () => (
              <p>
                Right now, the best way to change your billing date is to wait
                for the date that you'd like to be billed, upgrade or downgrade
                your account, and then change back to your original plan right
                away. This refreshes your billing date. Unfortunately, there
                isn't currently another way to reset the billing date at this
                time.
              </p>
            ),
          },
          contact: getDefaultFields(isLoggedIn),
        }),
      },
    ],
  }),
});

export const createOther = (isLoggedIn: boolean) => ({
  value: 'other',
  label: 'Other pricing or billing Issue',
  zendeskCustomFieldValue: 'cf-other-pricing-billing',
  child: help({
    docs: {
      title: 'Learn about Zapier’s plans and pricing',
      body: () => (
        <p>
          To learn more about about Zapier plans and other useful features
          available, check out our{' '}
          <Link href="/app/planbuilder/plans">Plans and Pricing guide</Link>.
        </p>
      ),
      links: [
        {
          href: '/app/planbuilder/plans',
          title: 'How do I view or change my plan?',
        },
        {
          href: 'https://help.zapier.com/hc/en-us/articles/8496277302157',
          title: 'What happens when I change my plan?',
        },
      ],
    },
    contact: getDefaultFields(isLoggedIn),
  }),
});

export const createTwofa = (isLoggedIn: boolean) => ({
  value: 'twofa',
  label: 'Troubleshoot two-factor authentication',
  zendeskCustomFieldValue: 'cf-troubleshoot-2fa',
  child: help({
    docs: {
      title: 'Check the help article on setting up two-factor authentication',
      body: () => (
        <p>
          Two-factor authentication is a powerful security feature. When
          enabled, it gives your Zapier account an extra layer of protection.
          For common questions about two-factor authentication,{' '}
          <Link href="https://help.zapier.com/hc/en-us/articles/8496305453069">
            check out our help docs
          </Link>
          . If you have it enabled and are having issues getting into your
          account, check out our tips on{' '}
          <Link href="https://help.zapier.com/hc/en-us/articles/8496305453069#9-finding-your-recovery-codes-0-8">
            troubleshooting two-factor authentication issues here
          </Link>
          .
        </p>
      ),
    },
    contact: getDefaultFields(isLoggedIn),
  }),
});

const taskUsageErrorHelp = (isLoggedIn: boolean) =>
  help({
    docs: {
      title: 'Check for Zap loops or surge in trigger data',
      body: () => (
        <Fragment>
          <p>
            There are a few reasons that a Zap may have used too many tasks:
          </p>
          <ol>
            <li>
              It experienced a Zap loop, where a Zap triggers itself or multiple
              Zaps trigger each other continuously. You can learn{' '}
              <Link href="https://help.zapier.com/hc/en-us/articles/8496232045453">
                how to fix that here
              </Link>
              .
            </li>
            <li>
              The trigger may have captured a bunch of records at once. This
              might happen if you make a change in your trigger app that affects
              every record, such as changing a spreadsheet column which affects
              every row. It might also happen if you recently imported a lot of
              information into your trigger app.
            </li>
          </ol>
          <p>
            Regardless of cause, we'll be happy to help with issues caused by
            this.
          </p>
        </Fragment>
      ),
    },
    contact: [
      ...getUserFields(isLoggedIn),
      {
        ...messageField,
        label: `Please describe the issue you're seeing in detail`,
        details: `What is happening? What workflow are you trying to accomplish with this Zap?`,
      },
      taskField,
      ...getCriticalIssueFields(isLoggedIn),
    ],
  });

export const createTask = (isLoggedIn: boolean) => ({
  value: 'usage',
  label: 'Task usage',
  zendeskCustomFieldValue: 'cf-task-usage',
  child: radio({
    id: 'usage',
    label: `What's the issue?`,
    zendeskCustomFieldId: ZENDESK_CUSTOM_FIELD_IDS.TASKS_WHATS_THE_ISSUE,
    items: [
      {
        value: 'more',
        label: 'I need more tasks on my plan',
        zendeskCustomFieldValue: 'cf-task-need-more',
        child: help({
          docs: {
            title: 'Upgrade your Zapier plan',
            body: () => (
              <p>
                To get more tasks on your plan, you'll need to upgrade your plan
                to the next one, which you can do{' '}
                <Link href="/app/planbuilder/plans">here</Link>.
              </p>
            ),
          },
          contact: [
            ...getUserFields(isLoggedIn),
            {
              ...messageField,
              label: `Let us know about any issues with your current task limit in detail.`,
              details: () => (
                <p>
                  You can also always check your usage{' '}
                  <Link href="/app/settings/billing/">
                    in your settings here
                  </Link>
                  .
                </p>
              ),
            },
            ...getCriticalIssueFields(isLoggedIn),
          ],
        }),
      },
      {
        value: 'error',
        label: 'My Zap erroneously used too many tasks',
        zendeskCustomFieldValue: 'cf-task-used-too-many',
        child: isLoggedIn
          ? zap({
              title: `Which Zap isn't working?`,
              instructions: `By selecting a Zap, we’ll be able to get back to you with a better and faster response. Please don't delete or modify the Zap until we've had the chance to take a look at it for you!`,
              kind: ['workflow'],
              kindDisplayName: 'Zap',
              child: taskUsageErrorHelp(isLoggedIn),
            })
          : taskUsageErrorHelp(isLoggedIn),
      },
    ],
  }),
});

export const createTrial = (isLoggedIn: boolean) => ({
  value: 'trial',
  label: 'Request a trial extension',
  zendeskCustomFieldValue: 'cf-trial-extension',
  child: help({
    contact: [
      ...getUserFields(isLoggedIn),
      {
        id: 'reason',
        label: `Please select the reason you'd like to extend your trial.`,
        type: 'dropdown',
        isRequired: true,
        items: [
          {
            value: 'time',
            label: `I didn't have enough time to explore Zapier features`,
          },
          {
            value: 'trouble',
            label: `I'm having trouble getting my automations started`,
          },
          {
            value: 'demo',
            label: `I need to demonstrate Zapier for my company`,
          },
          {
            value: 'premium',
            label: `There's a premium feature (premium apps, multi-step) that I'd like to try`,
          },
          {
            value: 'other',
            label: `Other`,
          },
        ],
      },
      ...getCriticalIssueFields(isLoggedIn),
    ],
  }),
});

export const createIssues = (isLoggedIn: boolean) => ({
  value: 'issues',
  label: 'Payment failure issues',
  zendeskCustomFieldValue: 'cf-payment-failure',
  child: help({
    docs: {
      title: 'Check if your payment method is valid',
      body: () => (
        <Fragment>
          <p>
            Sorry that you're experiencing payment issues! A payment failure can
            happen for several reasons, such as the card expiring. If a payment
            repeatedly fails but the card should work, contact your bank for
            more details and to lift any restrictions.
          </p>
          <p>
            When a payment fails, we will retry every three days automatically
            for 12 days, including after the payment information is updated
            again.
          </p>
        </Fragment>
      ),
    },
    contact: getDefaultFields(isLoggedIn),
  }),
});

export const createSettings = (isLoggedIn: boolean) => ({
  value: 'settings',
  label: 'Account access and settings',
  zendeskCustomFieldValue: 'cf-access-and-settings',
  child: radio({
    id: 'settings',
    label: "What's the issue?",
    zendeskCustomFieldId:
      ZENDESK_CUSTOM_FIELD_IDS.ACCESS_WHAT_WOULD_YOU_LIKE_TO_DO,
    items: [
      {
        value: 'owner',
        label: 'Change the account owner email',
        zendeskCustomFieldValue: 'cf-change-account-email',
        child: help({
          docs: {
            title: 'Change the account owner in your email settings',
            body: () => (
              <p>
                You can change the account owner email{' '}
                <Link href="/app/settings/manage-email">
                  in your email settings here
                </Link>
                . We will send an email confirmation to the new owner, who
                should confirm by logging into Zapier using the original owner's
                email and password.
              </p>
            ),
          },
          contact: getDefaultFields(isLoggedIn),
        }),
      },
      {
        value: 'notifications',
        label: 'Manage email notifications',
        zendeskCustomFieldValue: 'cf-manage-email-notifications',
        child: help({
          docs: {
            title: 'Manage your email notifications in your settings',
            body: () => (
              <p>
                You can manage the emails you receive from us{' '}
                <Link href="/app/settings/notifications">
                  in your email notification settings
                </Link>
                . There you can adjust your settings on how often you get
                emails, as well as which kinds of emails you get.
              </p>
            ),
          },
          contact: getDefaultFields(isLoggedIn),
        }),
      },
      createTwofa(isLoggedIn),
      {
        value: 'reset',
        label: 'Reset password',
        zendeskCustomFieldValue: 'cf-reset-password',
        child: help({
          docs: {
            title: 'Reset your password in your password settings',
            body: () => (
              <p>
                You can reset your password on your{' '}
                <Link href="/app/settings/password">
                  password settings here
                </Link>
                . If you do not currently have a password (which may be the case
                if you signed up with your Google account) but would like to set
                one up, <Link href="/app/login/forgot">you can do so here</Link>
                .
              </p>
            ),
          },
          contact: getDefaultFields(isLoggedIn),
        }),
      },
    ],
  }),
});

export const createDelete = (isLoggedIn: boolean) => ({
  value: 'delete',
  label: 'Delete an account',
  zendeskCustomFieldValue: 'cf-delete-account',
  child: help({
    docs: {
      title: 'Delete your Zapier account or downgrade to the Free plan',
      body: () => (
        <Fragment>
          <p>
            To delete your account,{' '}
            <Link href="/app/settings/delete">go here</Link>. Deleting your
            Zapier account is permanent and can't be undone, so all Zaps,
            developer apps, or account connections will be removed when you do
            this.
          </p>
          <p>
            If you want to keep your account active but no longer pay for
            Zapier, you can{' '}
            <Link href="/app/planbuilder/plans">
              downgrade to a free account here
            </Link>
            .
          </p>
        </Fragment>
      ),
    },
    contact: getDefaultFields(isLoggedIn),
  }),
});
